import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Mail, Loader2 } from 'lucide-react';

interface NewsletterSignupProps {
  variant?: 'footer' | 'modal';
  onSuccess?: () => void;
  formType?: 'newsletter';
}

export const NewsletterSignup: React.FC<NewsletterSignupProps> = ({ 
  variant = 'footer', 
  onSuccess,
  formType = 'newsletter'
}) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setMessage('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      // Use Formspree endpoint
      const formData = new FormData();
      formData.append('email', email);
      formData.append('formType', formType);
      formData.append('source', variant === 'modal' ? 'welcome-modal' : 'footer');
      
      const response = await fetch('https://formspree.io/f/meokllpe', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setMessage('Thank you for signing up! Check your email for confirmation.');
        setEmail('');
        // Store in session storage to remember signup for this session
        sessionStorage.setItem('newsletterSignedUp', 'true');
        // Call success callback if provided
        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error('Submission failed');
      }
      
    } catch (error) {
      setMessage('Something went wrong. Please try again.');
      console.error('Newsletter signup error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (variant === 'footer') {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-serif font-semibold text-sage-600 mb-2">
            Stay Connected
          </h3>
          <p className="text-sage-500 text-sm">
            Get fertility tips, wellness insights, and updates delivered to your inbox.
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1">
              <label htmlFor="footer-email" className="sr-only">
                Email address for newsletter signup
              </label>
            <Input
                id="footer-email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/10 border-sage-200 text-sage-50 placeholder:text-sage-200"
              disabled={isLoading}
                aria-describedby="footer-email-help"
            />
            </div>
            <Button 
              type="submit" 
              disabled={isLoading}
              className="bg-sage-500 hover:bg-sage-600 text-white"
              aria-label="Subscribe to newsletter"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Mail className="h-4 w-4" aria-hidden="true" />
              )}
            </Button>
          </div>
          
          {message && (
            <p id="footer-email-help" className={`text-sm ${message.includes('Thank you') ? 'text-green-400' : 'text-red-400'}`}>
              {message}
            </p>
          )}
        </form>
      </div>
    );
  }

  // Modal variant
  return (
    <div className="text-center space-y-6">
      <div className="space-y-4">
        <h2 className="text-3xl font-serif font-bold text-sage-800">
          Welcome to Women and the Way
        </h2>
        <p className="text-lg text-sage-700 max-w-md mx-auto">
          Join our community and receive fertility tips, wellness insights, and exclusive updates.
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="modal-email" className="sr-only">
            Email address for newsletter signup
          </label>
          <Input
            id="modal-email"
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full max-w-sm mx-auto border-sage-300"
            disabled={isLoading}
            aria-describedby="modal-email-help"
          />
          <Button 
            type="submit" 
            disabled={isLoading}
            className="w-full max-w-sm bg-sage-600 hover:bg-sage-700 text-white font-semibold"
            aria-label="Sign up for newsletter"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" aria-hidden="true" />
                Signing up...
              </>
            ) : (
              <>
                <Mail className="h-4 w-4 mr-2" aria-hidden="true" />
                Sign Up for Newsletter
              </>
            )}
          </Button>
        </div>
        
        {message && (
          <p id="modal-email-help" className={`text-sm ${message.includes('Thank you') ? 'text-green-600' : 'text-red-600'}`}>
            {message}
          </p>
        )}
      </form>
      
      <p className="text-xs text-sage-500">
        We respect your privacy. Unsubscribe at any time.
      </p>
    </div>
  );
}; 